import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import { GENERATE_REPORT } from "../../../api/apiUrls";
import axios from "axios";
import {
  buildCondition,
  getYearList,
  useMonthlyDateRanges,
} from "../../../static/hook";
import dayjs from "dayjs";
import { packageCodes, stStatusOptions } from "../../../static/dropdown";
import { useSearchParams } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

const SalesDashboard = () => {
  const [searchParams] = useSearchParams();
  const userCode = parseInt(searchParams.get("userCode"));
  const [dateFilterRange, setDateFilterRange] = useState([]);
  const [expandedYear, setExpandedYear] = useState(null);
  const [expandedQuarter, setExpandedQuarter] = useState(null);
  const [quarterMonthData, setQuarterMonthData] = useState(null);
  const [locationWiseData, setLocationWiseData] = useState([]);
  const [locationWiseLoading, setLocationWiseLoading] = useState([]);
  const [authState, setAuthState] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const rangeList = useMonthlyDateRanges(dateFilterRange);
  const [filterDropdown, setFilterDropdown] = useState({
    intakeOptions: [], // Example format: [{ name: 'Intake1', value: 1 }]
    categoryOptions: [],
  });
  const years = getYearList(2021, 2025);
  const [yearWiseData, setYearWiseData] = useState(null);
  const [quaterWiseLoading, setQuaterWiseLoading] = useState(null);
  const [quterWiseData, setQuaterWiseData] = useState(null);
  const [yearWiseLoading, setYearWiseLoading] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    package: [],
    intake: [],
    course: [],
    ststatus: [],
    category: [],
    paymentStatus: [],
  });
  const [year, setYear] = useState(2025);
  const [branchYearlyData, setBranchYearlyData] = useState([{ year: "" }]);
  const [branchQuaterData, setBranchQuaterData] = useState([
    { quarter: "", totalCount: 12 },
  ]);

  const fetchBaseQuery = useCallback(async (query) => {
    if (!query?.trim()) {
      console.warn("No query found to execute");
      return null;
    }
    try {
      const { data } = await axios.post(GENERATE_REPORT, { query });
      return data || null;
    } catch (error) {
      console.error(
        "Error occurred in fetchBaseQuery:",
        error?.response?.data?.message || error.message || "Unknown error"
      );
      return null;
    }
  }, []);

  const fetchFilterDropdown = useCallback(async () => {
    try {
      const queries = {
        intakeOptions:
          "SELECT IntakeCode AS 'Code', Intakename AS 'Name' FROM intakemas",
        categoryOptions:
          "SELECT CatCode AS 'Code', CatName AS 'Name' FROM catmas",
      };

      const fetchPromises = Object.entries(queries).map(
        async ([key, query]) => {
          const data = await fetchBaseQuery(query);
          return { key, data };
        }
      );

      const results = await Promise.all(fetchPromises);

      const updatedDropdowns = results.reduce((acc, { key, data }) => {
        if (data?.length) {
          acc[key] = data.map((item) => ({
            label: item.NAME || "Unknown",
            value: item.CODE || "N/A",
          }));
        } else {
          console.warn(`No data received for ${key}`);
        }
        return acc;
      }, {});

      if (Object.keys(updatedDropdowns).length) {
        setFilterDropdown((prev) => ({ ...prev, ...updatedDropdowns }));
      }
    } catch (error) {
      console.error("Error fetching filter dropdown options:", error.message);
    }
  }, [fetchBaseQuery]);

  const fetchDocumentsReportMonthWise = async () => {
    setLocationWiseLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const ststatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const courseTypeCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );
      const categoryTypeCondition = buildCondition(
        "s.catCode",
        filterOptions.category
      );

      [
        intakeCondition,
        ststatusCondition,
        packageCondition,
        locationCondition,
        courseTypeCondition,
        categoryTypeCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : ``;
      const responseObj = [];

      for (const item of rangeList) {
        try {
          const monthName = dayjs(item.start).format("MMMM YYYY");
          const dynamicQuery = `
          SELECT s.loccode, lo.locname, COUNT(DISTINCT s.StdCode) AS count
          FROM StudentMas s 
          JOIN locmas lo ON lo.loccode = s.loccode 
          WHERE s.RegistrDate != '' 
        ${whereClause} AND CAST(s.RegistrDate AS DATE) BETWEEN '${item.start}' AND '${item.end}' 
          GROUP BY s.loccode, lo.locname`;

          const result = await fetchBaseQuery(dynamicQuery);

          // Push structured month object to response array
          responseObj.push({
            month: monthName,
            data: result,
          });
        } catch (error) {
          console.error(
            `Error fetching data for ${item.start} to ${item.end}:`,
            error
          );
          responseObj.push({
            month: dayjs(item.start).format("MMMM YYYY"),
            start: item.start,
            end: item.end,
            data: [],
            error: true,
          });
        }
      }

      setLocationWiseData(responseObj);
    } catch (error) {
      console.log(error);
      setLocationWiseLoading(false);
    } finally {
      setLocationWiseLoading(false);
    }
  };
  const hasMonthData =
    Array.isArray(locationWiseData) && locationWiseData?.length > 0;
  const hasYearData = Array.isArray(yearWiseData) && yearWiseData?.length > 0;
  const hasQuaterData =
    Array.isArray(quterWiseData) && quterWiseData?.length > 0;

  const getDataYearWise = async () => {
    setYearWiseLoading(true);
    try {
      const filters = [];

      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake),
        buildCondition("s.packageCode", filterOptions.package),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("sd.DoctypeCode", filterOptions.checklist),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => {
        if (cond) filters.push(cond);
      });

      // Date Range Filter
      const rangeCondition =
        Array.isArray(dateFilterRange) &&
        dateFilterRange.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) {
        filters.push(rangeCondition);
      }

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";

      const queries = {
        fbdCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 2 ${whereClause}`,
        ggmCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 1 ${whereClause}`,
        chgCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 4 ${whereClause}`,
        bglBtmCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 5 ${whereClause}`,
        bglInCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 6 ${whereClause}`,
        chennaiCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 7 ${whereClause}`,
        mmACounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 8 ${whereClause}`,
      };

      const responseObj = [];

      for (const year of years) {
        const coordinatorData = {
          year: year,
          details: {},
        };

        for (const [key, baseQuery] of Object.entries(queries)) {
          const finalQuery = `${baseQuery} AND YEAR(s.RegistrDate) = ${year}`;

          try {
            const result = await fetchBaseQuery(finalQuery);
            coordinatorData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1,
            };
          } catch (error) {
            console.error(
              `Error fetching data for year ${year}, query ${key}:`,
              error
            );
          }
        }

        responseObj.push(coordinatorData);
      }
      setYearWiseData(responseObj);
      return responseObj;
    } catch (error) {
      console.error("Error in getDataYearWise:", error);
      setYearWiseLoading(false);
      return [];
    } finally {
      setYearWiseLoading(false);
    }
  };

  const getDataQuaterWise = async (selectedYear) => {
    setQuaterWiseLoading(true);
    try {
      const quaters = {
        quaterOne: [1, 2, 3],
        quaterTwo: [4, 5, 6],
        quaterThree: [7, 8, 9],
        quaterFour: [10, 11, 12],
      };

      const filters = [];

      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake),
        buildCondition("s.packageCode", filterOptions.package),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => {
        if (cond) filters.push(cond);
      });

      // Date Range Filter
      const rangeCondition =
        Array.isArray(dateFilterRange) &&
        dateFilterRange.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) {
        filters.push(rangeCondition);
      }

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";

      const queries = {
        fbdCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 2 ${whereClause}`,
        ggmCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 1 ${whereClause}`,
        chgCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 4 ${whereClause}`,
        bglBtmCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 5 ${whereClause}`,
        bglInCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 6 ${whereClause}`,
        chennaiCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 7 ${whereClause}`,
        mmACounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 8 ${whereClause}`,
      };

      const responseObj = [];

      for (const [quarterName, months] of Object.entries(quaters)) {
        const quarterData = {
          quarter: quarterName,
          details: {},
        };

        const monthCondition = months
          .map((m) => `MONTH(s.RegistrDate) = ${m}`)
          .join(" OR ");
        const dateClause = `AND YEAR(s.RegistrDate) = ${selectedYear} AND (${monthCondition})`;

        for (const [key, baseQuery] of Object.entries(queries)) {
          const finalQuery = `${baseQuery} ${dateClause}`;

          try {
            const result = await fetchBaseQuery(finalQuery);
            quarterData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1,
            };
          } catch (error) {
            console.error(
              `Error fetching data for ${quarterName}, query ${key}:`,
              error
            );
          }
        }

        responseObj.push(quarterData);
      }

      setQuaterWiseData(responseObj);
      return responseObj;
    } catch (error) {
      console.error("Error in getDataYearWise:", error);
      setQuaterWiseLoading(false);
      return [];
    } finally {
      setQuaterWiseLoading(false);
    }
  };

  const getQuaterMonthData = async (q, year) => {
    try {
      const quarterMap = {
        JFM: [1, 2, 3],
        AMJ: [4, 5, 6],
        JAS: [7, 8, 9],
        OND: [10, 11, 12],
      };

      const months = quarterMap[q] || [];
      if (!months.length) return null;

      const resultsByMonth = {
        quarter: q,
      };

      const filters = [];

      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake),
        buildCondition("s.packageCode", filterOptions.package),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => {
        if (cond) filters.push(cond);
      });

      // Date Range Filter
      const rangeCondition =
        Array.isArray(dateFilterRange) &&
        dateFilterRange.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) {
        filters.push(rangeCondition);
      }

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";

      const queries = {
        fbdCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 2 ${whereClause}`,
        ggmCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 1 ${whereClause}`,
        chgCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 4 ${whereClause}`,
        bglBtmCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 5 ${whereClause}`,
        bglInCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 6 ${whereClause}`,
        chennaiCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 7 ${whereClause}`,
        mmACounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 8 ${whereClause}`,
      };

      for (const month of months) {
        for (const [key, query] of Object.entries(queries)) {
          const finalQuery = `${query} AND MONTH(s.RegistrDate) = ${month} AND YEAR(s.RegistrDate) = ${year}`;
          try {
            const result = await fetchBaseQuery(finalQuery);
            const count = Number(result?.[0]?.Column1 || 0);

            const monthName = new Date(year, month - 1)
              .toLocaleString("default", {
                month: "short",
              })
              .toLowerCase(); // jan, feb, etc.

            if (!resultsByMonth[key]) {
              resultsByMonth[key] = {};
            }

            resultsByMonth[key][monthName] = count;
          } catch (error) {
            console.error(`Error fetching ${key} for month ${month}:`, error);
          }
        }
      }

      setQuarterMonthData(resultsByMonth);
      return resultsByMonth;
    } catch (error) {
      console.error("getQuaterMonthData failed:", error);
      return null;
    }
  };

  const handleChange = (index, value) => {
    const updatedRange = dateFilterRange;
    updatedRange[index] = value; // value will be in 'YYYY-MM-DD' format
    setDateFilterRange(updatedRange);
  };

  const handleFilterChange = () => {
    fetchFilterDropdown();
    fetchDocumentsReportMonthWise();
    getDataYearWise();
    getDataQuaterWise();
  };

  const checkAuthentication = async () => {
    try {
      if (userCode === 0) {
        setAuthState(true);
        return true;
      }

      const checkAdmin = await fetchBaseQuery(
        `SELECT * FROM usermas WHERE UserCode = ${userCode} AND deptcode IN (15, 16, 3, 11)`
      );

      if (Array.isArray(checkAdmin) && checkAdmin.length > 0) {
        setAuthState(true);
        return true;
      }

      setAuthState(false);
      return false;
    } catch (error) {
      console.error("Error checking authentication:", error);
      setAuthState(false);
      return false;
    }
  };

  const fetchSalesData = async () => {
    const yearsList = getYearList(2021, 2025);

    const quarterMap = {
      JFM: [1, 2, 3],
      AMJ: [4, 5, 6],
      JAS: [7, 8, 9],
      OND: [10, 11, 12],
    };

    const monthNames = {
      1: "Jan",
      2: "Feb",
      3: "Mar",
      4: "Apr",
      5: "May",
      6: "Jun",
      7: "Jul",
      8: "Aug",
      9: "Sep",
      10: "Oct",
      11: "Nov",
      12: "Dec",
    };

    const queries = {
      fbdCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 2`,
      ggmCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 1`,
      chgCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 4`,
      bglBtmCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 5`,
      bglInCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 6`,
      chennaiCounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 7`,
      mmACounts: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s JOIN locmas lo ON lo.loccode = s.loccode LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.loccode = 8`,
    };

    const tempData = [];

    for (const year of yearsList) {
      const yearData = {
        year,
        data: {},
        quarters: [],
      };

      // Yearly Data
      for (const [key, baseQuery] of Object.entries(queries)) {
        const yearQuery = `${baseQuery} AND YEAR(s.RegistrDate) = ${year}`;
        const res = await fetchBaseQuery(yearQuery);
        const count = res?.[0]?.Column1 || 0;

        yearData.data[key] = {
          query: yearQuery,
          count,
        };
      }
      console.log("first", yearData);
      // Quarterly and Monthly Data
      for (const [quarterName, months] of Object.entries(quarterMap)) {
        const quarterData = {
          quarter: quarterName,
          data: {},
          monthly: [],
        };

        for (const [key, baseQuery] of Object.entries(queries)) {
          const monthStr = months.join(",");
          const quarterQuery = `${baseQuery} AND YEAR(s.RegistrDate) = ${year} AND MONTH(s.RegistrDate) IN (${monthStr})`;
          const res = await fetchBaseQuery(quarterQuery);
          const count = res?.[0]?.Column1 || 0;

          quarterData.data[key] = {
            query: quarterQuery,
            count,
          };
        }
        console.log("quarterData", quarterData);

        // Monthly data per quarter
        for (const monthNum of months) {
          const monthLabel = monthNames[monthNum];
          const monthData = {
            month: monthLabel,
            data: {},
          };

          for (const [key, baseQuery] of Object.entries(queries)) {
            const monthQuery = `${baseQuery} AND YEAR(s.RegistrDate) = ${year} AND MONTH(s.RegistrDate) = ${monthNum}`;
            const res = await fetchBaseQuery(monthQuery);
            const count = res?.[0]?.Column1 || 0;

            monthData.data[key] = {
              query: monthQuery,
              count,
            };
          }

          quarterData.monthly.push(monthData);
        }

        yearData.quarters.push(quarterData);
      }

      tempData.push(yearData);
    }

    console.log("tempData", tempData);

    setSalesData(tempData);
  };

  useEffect(() => {
    checkAuthentication();
  }, [userCode]);

  useEffect(() => {
    fetchFilterDropdown();
    fetchDocumentsReportMonthWise();
  }, [rangeList]);

  useEffect(() => {
    fetchFilterDropdown();
    fetchDocumentsReportMonthWise();
    getDataYearWise();
    getDataQuaterWise();
    fetchSalesData();
  }, []);

  useEffect(() => {
    if (year) {
      getDataQuaterWise(year);
    }
  }, [filterOptions, dateFilterRange, year]);

  useEffect(() => {
    fetchDocumentsReportMonthWise();
    if (rangeList && rangeList.length > 0) {
      setDateRange(rangeList);
    }
  }, [rangeList]);

  const branchKeyMap = {
    Faridabad: "fbdCounts",
    Gurugram: "ggmCounts",
    "Bangalore-BTM": "bglBtmCounts",
    "Bangalore-Indiranagar": "bglInCounts",
    Chandigarh: "chgCounts",
    Chennai: "chennaiCounts",
    "Mumbai-Andheri": "mmACounts",
  };

  return authState ? (
    <>
      <div className="header py-3 border border-light px-2 rounded bg-light">
        <h5>Filter</h5>
        <div className="row g-2 g-lg-3 mb-3">
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="intake-select"
                options={filterDropdown.intakeOptions}
                isMulti
                isClearable
                placeholder="Select Intake"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    intake: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="ststatus-select"
                options={stStatusOptions}
                isMulti
                isClearable
                placeholder="Select Student Status"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    ststatus: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="category-select"
                options={filterDropdown.categoryOptions}
                isMulti
                isClearable
                placeholder="Select Student Category"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    category: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="package-select"
                options={packageCodes}
                isMulti
                isClearable
                placeholder="Select Package"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    package: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="course-select"
                options={[
                  { label: "Master's", value: "M" },
                  { label: "Bachelor's", value: "B" },
                ]}
                isMulti
                isClearable
                placeholder="Select Course"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    course: selectedOptions
                      ? selectedOptions.map((opt) => opt.value)
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          {/* <div className="col-md-1 text-end">
            <label htmlFor="">Select Range</label>
          </div>
          <div className="col-md-3">
            <div className="form-group d-flex">
              <input
                type="date"
                className="form-control"
                // value={dateFilterRange[0] || ""}
                onChange={(e) => handleChange(0, e.target.value)}
              />
              <input
                type="date"
                className="form-control"
                // value={dateFilterRange[1] || ""}
                onChange={(e) => handleChange(1, e.target.value)}
              />
            </div>
          </div> */}
          <div className="form-group col-md-2">
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={handleFilterChange}
            >
              Apply Filter
            </button>
          </div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div>
              <div className="row mb-2">
                <div className="col-6">
                  <span className="fs-5 fw-bold text-dark">Branch Data</span>
                </div>
              </div>
              {!yearWiseLoading ? (
                <div className="overflow-auto wb-scrollbar-w-1 mb-3">
                  {hasYearData ? (
                    <table className="table table-yesgermany table-bordered">
                      <thead>
                        <tr className="text-nowrap">
                          <th>Branch</th>
                          {yearWiseData.map((item) => {
                            const isYearExpanded = expandedYear === item.year;
                            return (
                              <React.Fragment key={item.year}>
                                {/* Year Column */}
                                <th
                                  className="text-center cursor-pointer"
                                  onClick={() => {
                                    setYear(item.year);
                                    setExpandedYear((prev) =>
                                      prev === item.year ? null : item.year
                                    );
                                    setExpandedQuarter(null); // reset quarter
                                  }}
                                >
                                  {item.year}
                                </th>

                                {/* Quarters if year expanded */}
                                {isYearExpanded &&
                                  ["JFM", "AMJ", "JAS", "OND"].map((q) => {
                                    const isQuarterExpanded =
                                      expandedQuarter === `${item.year}-${q}`;
                                    return (
                                      <React.Fragment key={`${item.year}-${q}`}>
                                        <th
                                          className="text-center text-light bg-secondary cursor-pointer"
                                          onClick={() => {
                                            const quarterKey = `${item.year}-${q}`;
                                            if (
                                              expandedQuarter === quarterKey
                                            ) {
                                              setExpandedQuarter(null);
                                            } else {
                                              setExpandedQuarter(quarterKey);
                                              getQuaterMonthData(q, item.year);
                                            }
                                          }}
                                        >
                                          {q}
                                        </th>

                                        {/* Months if quarter expanded */}
                                        {isQuarterExpanded &&
                                          {
                                            JFM: ["Jan", "Feb", "Mar"],
                                            AMJ: ["Apr", "May", "Jun"],
                                            JAS: ["Jul", "Aug", "Sep"],
                                            OND: ["Oct", "Nov", "Dec"],
                                          }[q].map((month) => (
                                            <th
                                              key={`${item.year}-${q}-${month}`}
                                              className=" text-light small text-center bg-success"
                                            >
                                              {month}
                                            </th>
                                          ))}
                                      </React.Fragment>
                                    );
                                  })}
                              </React.Fragment>
                            );
                          })}
                          <th className="text-center">Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {Object.keys(branchKeyMap).map(
                          (branchName, branchIdx) => {
                            let total = 0;
                            return (
                              <tr key={branchIdx} className="text-center">
                                <td className="text-start">{branchName}</td>
                                {yearWiseData.map((yearItem) => {
                                  const isYearExpanded =
                                    expandedYear === yearItem.year;
                                  const key = branchKeyMap[branchName];
                                  const count =
                                    yearItem.details?.[key]?.count ?? 0;
                                  total += count;

                                  return (
                                    <React.Fragment key={yearItem.year}>
                                      {/* Year column count */}
                                      <td>{count}</td>

                                      {/* Quarters if year is expanded */}
                                      {isYearExpanded &&
                                        ["JFM", "AMJ", "JAS", "OND"].map(
                                          (q, qIdx) => {
                                            const quarterKey = `quater${
                                              ["One", "Two", "Three", "Four"][
                                                qIdx
                                              ]
                                            }`;
                                            const quarterData =
                                              quterWiseData?.find(
                                                (qtr) =>
                                                  qtr.quarter === quarterKey
                                              );
                                            const qtrCount =
                                              quarterData?.details?.[key]
                                                ?.count ?? 0;
                                            const quarterUniqueKey = `${yearItem.year}-${q}`;
                                            const isQuarterExpanded =
                                              expandedQuarter ===
                                              quarterUniqueKey;

                                            return (
                                              <React.Fragment
                                                key={`${yearItem.year}-${q}`}
                                              >
                                                {/* Quarter count */}
                                                <td
                                                  className="bg-secondary text-light cursor-pointer"
                                                  onClick={() => {
                                                    if (
                                                      expandedQuarter ===
                                                      quarterUniqueKey
                                                    ) {
                                                      setExpandedQuarter(null);
                                                    } else {
                                                      setExpandedQuarter(
                                                        quarterUniqueKey
                                                      );
                                                      getQuaterMonthData(
                                                        q,
                                                        yearItem.year
                                                      ).then((res) => {
                                                        setQuarterMonthData(
                                                          res
                                                        );
                                                      });
                                                    }
                                                  }}
                                                >
                                                  {qtrCount}
                                                </td>

                                                {/* Months if quarter expanded */}
                                                {isQuarterExpanded &&
                                                  {
                                                    JFM: ["jan", "feb", "mar"],
                                                    AMJ: ["apr", "may", "jun"],
                                                    JAS: ["jul", "aug", "sep"],
                                                    OND: ["oct", "nov", "dec"],
                                                  }[q].map((mKey) => {
                                                    const monthCount =
                                                      quarterMonthData?.[key]?.[
                                                        mKey
                                                      ] ?? 0;
                                                    return (
                                                      <td
                                                        key={`${yearItem.year}-${q}-${mKey}`}
                                                        className="text-muted bg-success-subtle"
                                                      >
                                                        {monthCount}
                                                      </td>
                                                    );
                                                  })}
                                              </React.Fragment>
                                            );
                                          }
                                        )}
                                    </React.Fragment>
                                  );
                                })}
                                <td>
                                  <strong>{total}</strong>
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <p>No Data to Display</p>
                  )}
                </div>
              ) : (
                <div className="py-3">
                  <SkeletonTheme highlightColor="#fff3cd">
                    <Skeleton height={70} style={{ marginBottom: "10px" }} />
                  </SkeletonTheme>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  ) : (
    <></>
  );
};

export default SalesDashboard;
